import React, { useState, useCallback, useRef } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import UnitDialog from '../products/ProductDialog';
import ProductImportDialog from '../products/ProductImportDialog';

interface Unit {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  symbol: string;
  unitOfMeasurement: string;
  isSynced: boolean;
  createdAt: string;
  updatedAt: string;
  onHandQuantity: number;
  expectedQuantity: number;
  itemAdditionalProperties: Record<string, any>;
  itemMinNumber?: number | null;
  itemMaxNumber?: number | null;
}

const LS_KEY = 'units-dynamic-prop-keys';

const UnitsListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deactivateId, setDeactivateId] = useState<string | null>(null);
  const [deactivateUnit, setDeactivateUnit] = useState<Unit | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isZeroQuantities = (u?: Unit | null) =>
  !!u && (u.onHandQuantity ?? 0) === 0 && (u.expectedQuantity ?? 0) === 0;

  const askDeactivate = (unit: Unit) => {
  if (!isZeroQuantities(unit)) {
    dispatch(showAlert({
      message: 'Cannot deactivate: On-hand or Expected is not zero.',
      type: 'warning'
    }));
    return;
  }
  setDeactivateId(unit.id);
  setDeactivateUnit(unit);
};

const askDelete = (unit: Unit) => {
  if (!isZeroQuantities(unit)) {
    dispatch(showAlert({
      message: 'Cannot delete: On-hand or Expected is not zero.',
      type: 'warning'
    }));
    return;
  }
  setDeleteId(unit.id);
};

const handleDeactivate = async () => {
  if (!deactivateId) return;
  try {
    const res = await DataService.post(`api/Unit/${deactivateId}/deactivate`, {}); // POST with empty body
    if (res.ok) {
      dispatch(showAlert({ message: 'Unit deactivated successfully!', type: 'success' }));
      setRefreshKey((p) => p + 1);
    } else {
      const text = await res.text().catch(() => '');
      dispatch(showAlert({ message: `Failed to deactivate Unit! ${text}`, type: 'error' }));
    }
  } catch {
    dispatch(showAlert({ message: 'Failed to deactivate Unit!', type: 'error' }));
  } finally {
    setDeactivateId(null);
    setDeactivateUnit(null);
  }
};

  // ---------- Import Products ----------
  const handleImportProducts = () => {
    setImportDialogOpen(true);
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    setRefreshKey((p) => p + 1);
    dispatch(showAlert({ message: 'Products imported successfully!', type: 'success' }));
  };

  const canonicalize = (k: string) => k?.toLowerCase().trim();

const getCaseInsensitive = (obj: Record<string, any> | undefined, canonicalKey: string) => {
  if (!obj) return null;
  const exact = obj[canonicalKey];
  if (exact !== undefined) return exact;

  const found = Object.entries(obj).find(([kk]) => canonicalize(kk) === canonicalKey);
  return found ? found[1] : null;
};

  // ---------- Format headers nicely ----------
  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const specialCases: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season',
    };
    return specialCases[key] || formatted;
  }, []);

const initialSeenKeys = (() => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    const uniq = Array.isArray(arr) ? Array.from(new Set(arr.map((k: string) => canonicalize(k)))) : [];
    return uniq;
  } catch {
    return [];
  }
})();

const seenDynamicKeysRef = useRef<Set<string>>(new Set(initialSeenKeys));

const persistSeenKeys = (set: Set<string>) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set))); // already canonicalized
  } catch {}
};

  const buildColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const base: PaginatedDataGridColumn[] = [
        {
          field: 'barcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          groupable: false, // Barcode shouldn't be grouped
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: 'pointer', fontWeight: 'medium' }}
              onClick={() => navigate(`/units/${params.row.id}`)}
            >
              {params.value}
            </Typography>
          ),
        },
        { 
          field: 'itemName', 
          headerName: 'Item Name', 
          flex: 1.5, 
          minWidth: 160,
          groupable: true, 
        },
        { 
          field: 'symbol', 
          headerName: 'Symbol', 
          flex: 1, 
          minWidth: 120,
          groupable: true,
        },
        { 
          field: 'unitOfMeasurement', 
          headerName: 'Measurement', 
          flex: 1, 
          minWidth: 120,
          groupable: true,
        },
        { 
          field: 'expectedQuantity', 
          headerName: 'Expected', 
          width: 110, 
          type: 'number',
          groupable: true,
        },
        { 
          field: 'onHandQuantity', 
          headerName: 'On Hand', 
          width: 110, 
          type: 'number',
          groupable: true,
        },
        {
          field: 'isSynced',
          headerName: 'Synced',
          width: 100,
          groupable: true,
          renderCell: (params: GridRenderCellParams) => (
            <Chip
              label={params.value ? 'Yes' : 'No'}
              color={params.value ? 'success' : 'warning'}
              size="small"
              variant="outlined"
            />
          ),
        },
      ];

const dynamicCols = dynamicKeys.sort().map((ck) => {
  const prettyHeader = formatPropertyName(ck); 

      const col: PaginatedDataGridColumn = {
        field: ck,
        headerName: prettyHeader,
        flex: 1,
        minWidth: 110,
        valueGetter: (params) => getCaseInsensitive(params.row.itemAdditionalProperties, ck),
        sortable: true,
        groupable: true, // Additional properties can be grouped
      };

      if (ck.includes('color')) {
        col.columnType = 'color';
        col.renderCell = (params: GridRenderCellParams) => {
          if (!params.value) return <Typography color="text.secondary">-</Typography>;
          const colorValue = colorParser(params.value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: colorValue,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">{params.value}</Typography>
            </Box>
          );
        };
      }

      // Category/Brand/Season as chips
      if (['category', 'maincategory', 'brand', 'season'].some((f) => ck.includes(f))) {
        col.renderCell = (params: GridRenderCellParams) => {
          if (!params.value) return <Typography color="text.secondary">-</Typography>;
          return <Chip label={params.value} size="small" variant="outlined" color="primary" />;
        };
      }

      return col;
    });

      return [...base, ...dynamicCols];
    },
    [formatPropertyName, navigate]
  );

const [dynamicColumns, setDynamicColumns] = useState<PaginatedDataGridColumn[]>(
  buildColumns(Array.from(seenDynamicKeysRef.current))
);
  const mapFieldForGrouping = useCallback((field: string): string => {
    const canonicalField = canonicalize(field);
    
    if (seenDynamicKeysRef.current.has(canonicalField)) {
      return `Item.json:${canonicalField}`;
    }
    
    const fieldMapping: Record<string, string> = {
      'itemname': 'Item.Name',
      'symbol': 'Symbol',
      'unitofmeasurement': 'UnitOfMeasurement', 
      'issynced': 'IsSynced',
      'barcode': 'Barcode',
      'expectedquantity': 'ExpectedQuantity',
      'onhandquantity': 'OnHandQuantity',
    };
    
    return fieldMapping[canonicalField] || field;
  }, []);
  
  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);
      
      const isGrouped = params.grouping && params.grouping.GroupBy.length > 0;
      
      if (isGrouped && params.grouping) {
        params.grouping.GroupBy.forEach(field => {
          queryParams.append('GroupBy', mapFieldForGrouping(field));
        });
        queryParams.append('Level', String(params.grouping.Level));
        queryParams.append('GroupSortBy', params.grouping.GroupSortBy || 'key');
        queryParams.append('GroupSortDescending', String(params.grouping.GroupSortDescending || false));
        queryParams.append('IncludeCount', String(params.grouping.IncludeCount || true));
        
        if (params.grouping.ParentKey) {
          queryParams.append('ParentKey', params.grouping.ParentKey);
        }
      } else {
        if (params.sortModel?.length > 0) {
          const { field, sort } = params.sortModel[0];
          const canonicalField = canonicalize(field);
          const isDynamic = seenDynamicKeysRef.current.has(canonicalField);
          queryParams.append('SortBy', isDynamic ? `itemAdditionalProperties.${canonicalField}` : field);
          queryParams.append('SortDescending', String(sort === 'desc'));
        }
      }

      const endpoint = isGrouped ? 'api/Unit/grouped' : 'api/Unit';
      const res = await DataService.get(`${endpoint}?${queryParams.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch units`);
      const json = await res.json();

      let items: Unit[] = [];
      let groups: any[] = [];
      
      if (isGrouped) {
        if (json?.data?.groups) {
          groups = Array.isArray(json.data.groups) ? json.data.groups : Object.values(json.data.groups);
        } else if (Array.isArray(json?.groups)) {
          groups = json.groups;
        }
      } else {
        if (json?.data?.items) {
          if (Array.isArray(json.data.items)) items = json.data.items;
          else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
        } else if (Array.isArray(json?.items)) {
          items = json.items;
        }
        
        const newKeys: string[] = [];
        for (const u of items) {
          if (u.itemAdditionalProperties) {
            Object.keys(u.itemAdditionalProperties).forEach((k) => {
              const ck = canonicalize(k);
              if (!seenDynamicKeysRef.current.has(ck)) {
                seenDynamicKeysRef.current.add(ck);
                newKeys.push(ck);
              }
            });
          }
        }
        
        if (newKeys.length > 0) {
          persistSeenKeys(seenDynamicKeysRef.current);
          setDynamicColumns(buildColumns(Array.from(seenDynamicKeysRef.current)));
        }
      }


      return {
        items: isGrouped ? undefined : items,
        groups: isGrouped ? groups : undefined,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? (isGrouped ? groups.length : items.length),
        totalGroups: isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : undefined,
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : (json?.data?.totalCount ?? json?.totalCount ?? items.length)) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [buildColumns, mapFieldForGrouping]
  );

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'import',
      title: 'Import Products',
      icon: <FileUploadIcon />,
      onClick: handleImportProducts,
      disabled: () => false,
      color: 'primary',
    },
  ];

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await DataService.delete(`api/Unit/${deleteId}`, {});
      if (res.ok) {
        dispatch(showAlert({ message: 'Unit deleted successfully!', type: 'success' }));
        setRefreshKey((p) => p + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
    }
    setDeleteId(null);
  };

  const handleRowClick = (params: any) => navigate(`/Units/${params.row.id}`);

  const handleEditUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = (updatedUnit: any) => {
    dispatch(showAlert({ message: 'Unit updated successfully!', type: 'success' }));
    setRefreshKey((p) => p + 1);
    setEditDialogOpen(false);
    setSelectedUnit(null);
  };

  return (
    <>
      <PaginatedDataGrid
        key={refreshKey}
        title="Units"
        columns={dynamicColumns}
        idField="id"
        useBackendPagination
        onDataFetch={fetchUnits}
        cacheTimeout={300000}
        actions={actions}
        enableGrouping={true}
        enableGroupDrillDown={true}
        fieldMapper={mapFieldForGrouping}
        wmsMode
        groupedUrl='api/Unit/grouped/'
        densityLevel="compact"
        striped
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        maxHeight={700}
        onRowClick={handleRowClick}
        rowActions={[
          {
            key: 'edit',
            icon: <EditIcon />,
            label: 'Edit Unit',
            color: 'primary',
            onClick: (_id: GridRowId, row: any) => handleEditUnit(row),
          },
          {
            key: 'deactivate',
            icon: <BlockIcon />,
            label: 'Deactivate',
            color: 'warning',
            // If your PaginatedDataGrid supports disabled per-row, keep this:
            disabled: (row: Unit) => !isZeroQuantities(row),
            onClick: (_id: GridRowId, row: Unit) => askDeactivate(row),
          },
          {
            key: 'delete',
            icon: <DeleteIcon />,
            label: 'Delete',
            color: 'error',
            // If your PaginatedDataGrid supports disabled per-row, keep this:
            disabled: (row: Unit) => !isZeroQuantities(row),
            onClick: (_id: GridRowId, row: Unit) => askDelete(row),
          },
        ]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />

      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Unit"
        message="Are you sure you want to delete this Unit? This action cannot be undone."
      />

      <UnitDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUnit(null);
        }}
        unit={selectedUnit ? {
          ...selectedUnit,
          name: selectedUnit.itemName,
          additionalProperties: selectedUnit.itemAdditionalProperties,
          minNumber: selectedUnit.itemMinNumber,
          maxNumber: selectedUnit.itemMaxNumber,
          hasProperties: !!selectedUnit.itemAdditionalProperties && Object.keys(selectedUnit.itemAdditionalProperties).length > 0,
          propertyCount: selectedUnit.itemAdditionalProperties ? Object.keys(selectedUnit.itemAdditionalProperties).length : 0
        } : null}
        mode="edit"
        onSuccess={handleEditSuccess}
      />

      <ProductImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={handleImportSuccess}
      />

      <SimpleConfirmationDialog
        open={!!deactivateId}
        onClose={() => {
          setDeactivateId(null);
          setDeactivateUnit(null);
        }}
        onConfirm={handleDeactivate}
        title="Deactivate Unit"
        message={
          deactivateUnit
            ? `Are you sure you want to deactivate unit '${deactivateUnit.barcode}'?`
            : 'Are you sure you want to deactivate this unit?'
        }
      />
    </>
  );
};

export default UnitsListing;
